// Componente de cabecera para escanear un carrito en la recepción de rehusados
// Renderiza un input con placeholder e icono y un texto de ayuda inferior
import { faBarcode, faBoxArchive } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuthData } from '@modules/Auth';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { useToast } from '@shared/hooks/use-toast';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrderResponse } from '../../../adapters';
import { useCouriers, useOrderById } from '../../../hooks';
import { useAssignCart } from '../../../hooks/useAssignCart';
import { useCartData } from '../../../hooks/useCartData';

interface ScanCartSectionProps {
  returnedId: number;
  returnedData?: any; // Datos del rehusado para obtener location_id
  onOrderFound?: (order: OrderResponse) => void;
  hasOrder?: boolean;
  onProductScan?: (code: string) => void;
}

export const ScanCartSection = ({
  returnedId,
  returnedData,
  onOrderFound,
  hasOrder = false,
  onProductScan,
}: ScanCartSectionProps) => {
  const { t } = useTranslation('receiveModule');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const confirmModalRef = useRef<any>(null);
  const [cartCode, setCartCode] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [searchBarcode, setSearchBarcode] = useState<string | null>(null);
  const [selectedCourierId, setSelectedCourierId] = useState<string>('');
  const [scanAttempt, setScanAttempt] = useState<number>(0);
  const { toast } = useToast();

  // 🎯 Obtener datos del usuario desde el store de autenticación
  const { currentUser } = useAuthData();

  // 🎯 Hook para asignar carrito
  const { assignCart, isAssigning } = useAssignCart(returnedId);

  // 🎯 Hook para obtener lista de couriers
  const { data: couriers = [], isLoading: isLoadingCouriers } = useCouriers();

  // 🎯 Verificar si la opción de carrito está activa
  const isCartEnabled = (currentUser as any)?.settings?.carts?.is_active === true;

  // 🎯 Hook para obtener datos del carrito si ya existe
  const { cartData, isLoading: isLoadingCart } = useCartData(isCartEnabled ? returnedData?.location_id || null : null);

  // 🎯 Inicializar carrito si ya existe en los datos del rehusado
  useEffect(() => {
    if (cartData?.code && !cartCode) {
      setCartCode(cartData.code);
    }
  }, [cartData, cartCode]);

  // Buscar orden cuando haya un barcode y courier_id
  const {
    data: orderData,
    error,
    isFetching,
  } = useOrderById(searchBarcode, selectedCourierId ? Number(selectedCourierId) : null, scanAttempt);

  // Enfocar el input al montar la sección y cuando cambie el estado
  useEffect(() => {
    inputRef.current?.focus();
  }, [cartCode, hasOrder]); // Reenfocar cuando cambie cartCode o hasOrder

  // Manejar respuesta de la búsqueda de orden
  useEffect(() => {
    if (!searchBarcode) return;
    if (orderData) {
      // Validar que tenga trazabilidad
      if (orderData.shipment_statuses_count === 0) {
        confirmModalRef.current?.open(
          () => {
            // Callback cuando se acepta la alerta - Añadir rehusado de todas formas
            onOrderFound?.(orderData);
            setSearchBarcode(null);
            setScanAttempt(0); // Resetear intento
            setTimeout(() => {
              inputRef.current?.focus();
            }, 100);
          },
          () => {
            // Callback cuando se cancela
            setSearchBarcode(null);
            setScanAttempt(0); // Resetear intento
            setTimeout(() => {
              inputRef.current?.focus();
            }, 100);
          },
          t('toasts.no_tracking.title'),
          t('toasts.no_tracking.message'),
          t('toasts.no_tracking.confirm'),
        );
        return;
      }

      // Ya no validamos el status aquí porque el backend lo hace
      onOrderFound?.(orderData);
      setSearchBarcode(null);
      setScanAttempt(0); // Resetear intento

      // Reenfocar el input después de encontrar una orden
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [orderData, searchBarcode, onOrderFound, t, toast]);

  // Manejar error de búsqueda
  useEffect(() => {
    if (error && searchBarcode) {
      // Extraer mensaje específico devuelto por la API
      // Preferimos `response.data.message`; como fallback, concatenamos `errors`
      const apiMessage = (error as any)?.response?.data?.message || (error as any)?.data?.message;
      const apiErrors = (error as any)?.response?.data?.errors || (error as any)?.data?.errors;
      const normalizedErrors = apiErrors ? Object.values(apiErrors).flat().filter(Boolean).join(' ') : undefined;
      const description = apiMessage || normalizedErrors || (error as any)?.message || t('toasts.error.unexpected');

      // Log para debugging siguiendo convención del proyecto
      console.log('🔍 ScanCartSection - API error:', {
        message: (error as any)?.message,
        response: (error as any)?.response,
        data: (error as any)?.response?.data,
      });

      toast({
        title: t('toasts.error.title'),
        description,
        variant: 'destructive',
      });
      setSearchBarcode(null);
      setScanAttempt(0); // Resetear intento

      // Reenfocar el input después de un error
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [error, searchBarcode, t, toast]);

  // 🎯 Cobertura de loading específico del carrito
  if (isCartEnabled && returnedData?.location_id && isLoadingCart && !cartCode) {
    return (
      <div className="w-full flex items-center justify-center p-6">
        <div className="text-complementary-text text-sm">{t('loading.default')}</div>
      </div>
    );
  }

  return (
    <div data-testid="scan-cart-section">
      {/* Scan Row - Fila con badge de carrito + input */}
      <div className="flex flex-col gap-4">
        {/* Badge de carrito si está activo */}
        {isCartEnabled && cartCode && (
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center gap-3 bg-bg-info text-info rounded-full px-4 py-1.5 text-base select-none"
              data-testid="current-cart-badge"
            >
              <FontAwesomeIcon icon={faBoxArchive} className="w-4 h-4" />
              <span className="font-medium tracking-wide">{cartCode}</span>
            </span>
          </div>
        )}

        {/* Selector de mensajería - Mostrar solo cuando no hay pedido seleccionado y (no hay carrito requerido O carrito ya asignado) */}
        {!hasOrder && (!isCartEnabled || cartCode) && (
          <div className="space-y-2">
            <Label className="text-foreground">{t('scan.courier_label')}</Label>
            <Combobox
              options={couriers.map((c) => ({
                label: c.name,
                value: c.id,
              }))}
              placeholder={isLoadingCouriers ? t('loading.default') : t('scan.courier_placeholder')}
              value={selectedCourierId}
              setValue={setSelectedCourierId}
              disabled={isLoadingCouriers || isFetching || isAssigning}
              className="w-full"
            />
          </div>
        )}

        {/* Scan Input - Campo para escanear */}
        <div className="relative max-w-xl flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-icons">
            <FontAwesomeIcon icon={faBarcode} className="w-3.5 h-3.5" />
          </span>
          <Input
            placeholder={
              !isCartEnabled
                ? hasOrder
                  ? t('scan.product_placeholder')
                  : t('scan.order_placeholder')
                : cartCode
                  ? hasOrder
                    ? t('scan.product_placeholder')
                    : t('scan.order_placeholder')
                  : t('scan.cart_placeholder')
            }
            data-testid="scan-cart-input"
            className="w-full pl-9"
            autoFocus
            ref={inputRef}
            value={inputValue}
            disabled={!!isFetching || !!isAssigning}
            aria-busy={!!isFetching || !!isAssigning}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const value = inputValue.trim();
                if (!value) return;

                // Si la opción de carrito no está activa, ir directamente a escanear pedidos
                if (!isCartEnabled) {
                  if (!hasOrder) {
                    // Validar que haya mensajería seleccionada
                    if (!selectedCourierId) {
                      toast({
                        title: t('toasts.error.title'),
                        description: t('scan.courier_required'),
                        variant: 'destructive',
                      });
                      return;
                    }
                    // Escaneo de pedido
                    setScanAttempt((prev) => prev + 1); // Incrementar intento
                    setSearchBarcode(value);
                  } else {
                    // Escaneo de producto
                    onProductScan?.(value);
                  }
                } else {
                  // Lógica con carrito
                  if (!cartCode) {
                    // 🎯 Asignar carrito a la recepción
                    assignCart(value, {
                      onSuccess: () => {
                        // Solo establecer el carrito si la API fue exitosa
                        setCartCode(value);
                      },
                      onError: () => {
                        // No hacer nada, mantener en el paso de carrito
                        // El toast de error ya se muestra en el hook
                      },
                    });
                  } else {
                    if (!hasOrder) {
                      // Validar que haya mensajería seleccionada
                      if (!selectedCourierId) {
                        toast({
                          title: t('toasts.error.title'),
                          description: t('scan.courier_required'),
                          variant: 'destructive',
                        });
                        return;
                      }
                      // Escaneo de pedido
                      setScanAttempt((prev) => prev + 1); // Incrementar intento
                      setSearchBarcode(value);
                    } else {
                      // Escaneo de producto
                      onProductScan?.(value);
                    }
                  }
                }
                setInputValue('');
                // mantener foco para siguiente escaneo
                setTimeout(() => {
                  inputRef.current?.focus();
                }, 100);
              }
            }}
          />
        </div>
      </div>

      {/* Helper text - Texto de ayuda */}
      <p className="mt-6 text-complementary-text text-sm text-center">
        {!isCartEnabled
          ? hasOrder
            ? t('scan.product_helper')
            : isFetching || isAssigning
              ? t('scan.processing')
              : selectedCourierId
                ? t('scan.order_helper')
                : t('scan.courier_helper')
          : cartCode
            ? hasOrder
              ? t('scan.product_helper')
              : isFetching || isAssigning
                ? t('scan.processing')
                : selectedCourierId
                  ? t('scan.order_helper')
                  : t('scan.courier_helper')
            : t('scan.cart_helper')}
      </p>

      {/* Confirm Modal para validaciones */}
      <ConfirmModal ref={confirmModalRef} />
    </div>
  );
};
